import { useCallback } from 'react';

import { type InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { personaService } from '@/shared/api';
import type { GetPersonasResponse, PersonaData } from '@/shared/api/persona-service';
import { logger } from '@/shared/lib/logger';

export const useInfinitePersonas = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['personas', 'infinite'],
    queryFn: async ({ pageParam }) => {
      const response = await personaService.getPersonas(16, pageParam as string);
      return response;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const hasNext = lastPage.data.pageInfo?.hasNextPage;
      const nextCursor = lastPage.data.pageInfo?.nextCursor;
      return hasNext ? nextCursor : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Prevent race conditions during refetch
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    // Only notify when data actually changes, not during intermediate states
    notifyOnChangeProps: ['data', 'error', 'isLoading'],
    // Add retry logic for network failures
    retry: (failureCount, error: unknown) => {
      // Don't retry on 4xx errors (client errors)
      if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = error as { response: { status: number } };
        if (errorResponse.response.status >= 400 && errorResponse.response.status < 500) {
          return false;
        }
      }
      // Retry up to 3 times for network/server errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isAuthenticated, // Only fetch when authenticated
  });

  // Flatten all pages into single array with validation
  const personas =
    data?.pages.flatMap((page) => {
      if (!page?.data?.personas || !Array.isArray(page.data.personas)) {
        logger.persona.warn('Invalid persona page data', { page });
        return [];
      }
      // Filter out personas with missing critical data
      return page.data.personas.filter((persona: PersonaData) => {
        if (!persona || typeof persona.id !== 'number') {
          logger.persona.warn('Persona missing id', { persona });
          return false;
        }
        // Warn about missing names but don't filter them out completely
        if (!persona.name?.trim()) {
          logger.persona.warn('Persona missing name', { id: persona.id, name: persona.name });
        }
        return true;
      });
    }) || [];

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Add new persona to the beginning of the list (optimistic update)
  const addPersonaToCache = useCallback(
    (newPersona: PersonaData) => {
      queryClient.setQueryData(
        ['personas', 'infinite'],
        (oldData: InfiniteData<GetPersonasResponse> | undefined) => {
          if (!oldData) return oldData;

          // Add new persona to the first page
          const newPages = [...oldData.pages];
          if (newPages[0]) {
            newPages[0] = {
              ...newPages[0],
              data: {
                ...newPages[0].data,
                personas: [newPersona, ...newPages[0].data.personas],
              },
            };
          }

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    },
    [queryClient],
  );

  // Update existing persona in cache (optimistic update)
  const updatePersonaInCache = useCallback(
    (updatedPersona: PersonaData) => {
      queryClient.setQueryData(
        ['personas', 'infinite'],
        (oldData: InfiniteData<GetPersonasResponse> | undefined) => {
          if (!oldData) {
            return oldData;
          }

          // Update persona in all pages
          const newPages = oldData.pages.map((page) => ({
            ...page,
            data: {
              ...page.data,
              personas: page.data.personas.map((persona: PersonaData) => {
                if (persona.id === updatedPersona.id) {
                  return updatedPersona;
                }
                return persona;
              }),
            },
          }));

          const result = {
            ...oldData,
            pages: newPages,
          };
          return result;
        },
      );
    },
    [queryClient],
  );

  // Delete persona from cache (optimistic update)
  const deletePersonaFromCache = useCallback(
    (personaId: number) => {
      queryClient.setQueryData(
        ['personas', 'infinite'],
        (oldData: InfiniteData<GetPersonasResponse> | undefined) => {
          if (!oldData) return oldData;

          // Remove persona from all pages
          const newPages = oldData.pages.map((page) => ({
            ...page,
            data: {
              ...page.data,
              personas: page.data.personas.filter(
                (persona: PersonaData) => persona.id !== personaId,
              ),
            },
          }));

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    },
    [queryClient],
  );

  // Invalidate queries to refetch fresh data
  const invalidatePersonas = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['personas', 'infinite'] });
  }, [queryClient]);

  return {
    personas,
    isInitialLoading: isLoading,
    isLoadingMore: isFetchingNextPage,
    isError,
    error,
    hasNextPage: !!hasNextPage,
    loadMore,
    refetch,
    addPersonaToCache,
    deletePersonaFromCache,
    updatePersonaInCache,
    invalidatePersonas,
  };
};
